import * as React from 'react';
import { styled } from '@linaria/react';

import SvgWithGrid from '@/components/SvgWithGrid';

import { ARC_COLOR } from './ArcConstants';
import ColorModeContainer from '@/components/ColorModeContainer';

interface Props {
  ball: 'golf' | 'baseball' | 'bowling';
}

const P1 = { x: 10, y: 24 };
const P2 = { x: 22, y: 24 };

function ArcRadiusDemo({ ball }: Props) {
  let r = 0;

  if (ball === 'golf') {
    r = 6.5;
  } else if (ball === 'baseball') {
    r = 9.5;
  } else if (ball === 'bowling') {
    r = 25;
  }

  const d = `
    M 0,${P1.y}
    L ${P1.x},${P1.y}
    A ${r},${r} 0 0 0 ${P2.x},${P2.y}
    L 32,${P2.y}
  `;

  return (
    <Wrapper colorMode="dark">
      <BackgroundGrid
        gridGap={2}
        lineWidth={0.1}
        viewBox="0 0 32 32"
      />
      {ball === 'golf' && (
        <GolfBall
          src="/images/interactive-guide-to-paths/golf-ball.png"
          alt="Golf ball"
        />
      )}
      {ball === 'baseball' && (
        <Baseball
          src="/images/interactive-guide-to-paths/baseball.png"
          alt="Baseball"
        />
      )}
      {ball === 'bowling' && (
        <BowlingBall
          src="/images/interactive-guide-to-paths/bowlingball.png"
          alt="bowling"
        />
      )}
      <Svg viewBox="0 0 32 32">
        <Path d={d} />
        <Joint r={0.625} cx={P1.x} cy={P1.y} />
        <Joint r={0.625} cx={P2.x} cy={P2.y} />
      </Svg>
    </Wrapper>
  );
}

const Wrapper = styled(ColorModeContainer)`
  position: relative;
  width: fit-content;
  margin-inline: auto;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const Ball = styled.img`
  position: absolute;
  left: 0;
  right: 0;
  margin: auto;
  max-width: revert;
`;

const GolfBall = styled(Ball)`
  bottom: 14%;
  width: 38%;
  height: 38%;
`;

const Baseball = styled(Ball)`
  bottom: 18%;
  left: 2%;
  width: 73%;
  height: 73%;
`;

const BowlingBall = styled(Ball)`
  --size: 150%;
  bottom: 24%;
  width: var(--size);
  height: var(--size);
  left: calc(50% - var(--size) / 2);
  right: calc(50% - var(--size) / 2);
`;

const BackgroundGrid = styled(SvgWithGrid)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const Svg = styled.svg`
  position: relative;
  fill: none;
  width: 100%;
  max-width: 300px;
  aspect-ratio: 1 / 1;
  margin-inline: auto;
  outline: 1px solid var(--color-gray-300);
  outline-offset: -1px;
  border-radius: 4px;
`;

const Path = styled.path`
  fill: none;
  stroke: ${ARC_COLOR};
  stroke-width: 1px;
  stroke-linejoin: round;
`;

const Joint = styled.circle`
  fill: white;
  stroke: var(--color-background);
  stroke-width: 0.25px;
`;

export default ArcRadiusDemo;
